import { message } from 'antd';
import api from '../../api/client';
import type { ServiceOrder } from '../../api/serviceOrders';

// Кнопка «Провести в 1С» в меню «⋯» карточки заказа (оба вида)
// (direct_posting.md §7 rev. №6, решение владельца 2026-10-08):
// - пункт виден только для существующего НЕпроведённого заказа,
//   при праве direct_posting:use и включённых флагах master + service_order;
// - флаг OFF → пункта нет (как и кнопки в спеке);
// - клик → команда в очередь → опрос статуса → тост результата → перезагрузка заказа.

let availabilityCache: { at: number; canPost: boolean } | null = null;

export const getOrderPostingAvailable = async (): Promise<boolean> => {
  if (availabilityCache && Date.now() - availabilityCache.at < 60_000) return availabilityCache.canPost;
  let canPost = false;
  try {
    const r = await api.get('/posting/settings');
    const flags: Record<string, boolean> = r.data?.flags || {};
    canPost = !!flags['direct_posting.master'] && !!flags['direct_posting.service_order'];
  } catch {
    canPost = false; // нет права direct_posting:use или контур без direct_posting
  }
  availabilityCache = { at: Date.now(), canPost };
  return canPost;
};

export const invalidatePostingAvailability = (): void => {
  availabilityCache = null;
  receiptAvailabilityCache = null;
  printAvailabilityCache = null;
  invoiceAvailabilityCache = null;
};

let printAvailabilityCache: { at: number; canPrint: boolean } | null = null;

export const getOrderPrintAvailable = async (): Promise<boolean> => {
  if (printAvailabilityCache && Date.now() - printAvailabilityCache.at < 60_000) return printAvailabilityCache.canPrint;
  let canPrint = false;
  try {
    const r = await api.get('/posting/settings');
    const flags: Record<string, boolean> = r.data?.flags || {};
    canPrint = !!flags['direct_posting.master'] && !!flags['direct_posting.print'];
  } catch {
    canPrint = false;
  }
  printAvailabilityCache = { at: Date.now(), canPrint };
  return canPrint;
};

let receiptAvailabilityCache: { at: number; canFiscalize: boolean } | null = null;

// Доступность «Пробить чек» — master + флаг receipt (схема §7 rev. №8).
export const getReceiptFiscalizeAvailable = async (): Promise<boolean> => {
  if (receiptAvailabilityCache && Date.now() - receiptAvailabilityCache.at < 60_000) return receiptAvailabilityCache.canFiscalize;
  let canFiscalize = false;
  try {
    const r = await api.get('/posting/settings');
    const flags: Record<string, boolean> = r.data?.flags || {};
    canFiscalize = !!flags['direct_posting.master'] && !!flags['direct_posting.receipt'];
  } catch {
    canFiscalize = false;
  }
  receiptAvailabilityCache = { at: Date.now(), canFiscalize };
  return canFiscalize;
};

let invoiceAvailabilityCache: { at: number; canInvoice: boolean } | null = null;

export const getTaxInvoiceAvailable = async (): Promise<boolean> => {
  if (invoiceAvailabilityCache && Date.now() - invoiceAvailabilityCache.at < 60_000) return invoiceAvailabilityCache.canInvoice;
  let canInvoice = false;
  try {
    const r = await api.get('/posting/settings');
    const flags: Record<string, boolean> = r.data?.flags || {};
    canInvoice = !!flags['direct_posting.master'] && !!flags['direct_posting.invoice'];
  } catch {
    canInvoice = false;
  }
  invoiceAvailabilityCache = { at: Date.now(), canInvoice };
  return canInvoice;
};

// Awaitable-ядро выполнения команды direct_posting (OilChangePage.md №62):
// создание команды → опрос до done/error/timeout, без тостов и без reload.
// Используется окном «Провести и оплатить» (цепочка шагов) и обёртками ниже.
export interface CommandOutcome {
  status: 'done' | 'error' | 'timeout';
  message?: string;
  error?: string;
  orderPatch?: Partial<ServiceOrder>;
}

export interface CommandCreationOutcome {
  id: number | null;
  error?: string;
}

export const createPostingCommand = async (
  type: string,
  orderId: number,
  params?: Record<string, string | boolean>,
): Promise<CommandCreationOutcome> => {
  try {
    const r = await api.post('/posting/commands', { type, order_id: orderId, params });
    const id = r.data?.id ?? null;
    return id == null
      ? { id: null, error: 'Сервер не вернул id созданной команды' }
      : { id };
  } catch (e: any) {
    return { id: null, error: e?.response?.data?.detail || 'Команда отклонена' };
  }
};

export const waitPostingCommand = async (
  cmdId: number,
  timeoutMs = 90_000,
  pollIntervalMs = 1000,
): Promise<CommandOutcome> => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const r = await api.get(`/posting/commands/${cmdId}`);
      const cmd = r.data;
      if (cmd?.status === 'done') {
        return {
          status: 'done',
          message: cmd.result?.message,
          ...(cmd.result?.order_patch ? { orderPatch: cmd.result.order_patch } : {}),
        };
      }
      if (cmd?.status === 'error' || cmd?.status === 'cancelled') {
        return { status: 'error', error: cmd.error || 'Команда завершилась ошибкой' };
      }
    } catch {
      // Транзитная ошибка чтения не меняет состояния самой команды — продолжаем опрос.
    }
    if (Date.now() < deadline) await new Promise((r) => setTimeout(r, pollIntervalMs));
  }
  return {
    status: 'timeout',
    error: `Команда № ${cmdId} ещё выполняется или её результат не доставлен. `
      + 'Проверьте агента и статус в 1С; не запускайте операцию повторно без сверки.',
  };
};

export interface ChainCommandInput {
  key: string;
  type: string;
  params?: Record<string, string | boolean>;
}

export const createPostingChain = async (
  orderId: number,
  chainId: string,
  commands: ChainCommandInput[],
): Promise<{ ids: Record<string, number>; errors: Record<string, string> }> => {
  const ids: Record<string, number> = {};
  const errors: Record<string, string> = {};
  for (const command of commands) {
    const created = await createPostingCommand(command.type, orderId, {
      ...(command.params || {}),
      chain_id: chainId,
    });
    if (created.id == null) {
      errors[command.key] = created.error || 'Команда не создана';
      break;
    }
    ids[command.key] = created.id;
  }
  return { ids, errors };
};

export const runPostingCommand = async (
  type: string,
  orderId: number,
  params?: Record<string, string | boolean>,
  timeoutMs = 90_000,
  pollIntervalMs = 1000,
): Promise<CommandOutcome> => {
  const created = await createPostingCommand(type, orderId, params);
  if (created.id == null) {
    return { status: 'error', error: created.error || 'Команда не создана' };
  }
  return waitPostingCommand(created.id, timeoutMs, pollIntervalMs);
};

export const postOrderIn1C = async (orderId: number, onDone?: () => void): Promise<void> => {
  const key = `post-1c-${orderId}`;
  message.loading({ content: 'Команда отправлена — ждём 1С…', key, duration: 0 });
  const res = await runPostingCommand('post_service_order', orderId, undefined, 60_000);
  if (res.status === 'done') {
    message.success({ content: res.message || 'Проведён в 1С', key, duration: 5 });
    onDone?.();
  } else if (res.status === 'error') {
    message.error({ content: res.error, key, duration: 8 });
  } else {
    message.warning({ content: '1С пока не ответила — проверьте агента на ПК кассира', key, duration: 6 });
  }
};

// «Пробить чек товаров» / «Пробить чек услуг» (direct_posting.md §7 rev. №8 + №11):
// виден только для существующего заказа с созданным чеком ККМ соответствующего
// вида (товарный — external_1c_cash_receipt_id, услуг — external_1c_service_receipt_id),
// который ещё не пробит (receipt_posted / service_receipt_posted === false),
// при праве direct_posting:use и включённых флагах master + receipt
// (доступность — getReceiptFiscalizeAvailable).
// Оплата — из шапки заказа: карта → payment_type 'card', иначе 'cash';
// терминал подставляет backend по виду чека (№11), frontend его не шлёт.
export const fiscalizeReceipt = async (
  orderId: number,
  opts: { cardPayment?: boolean; receiptKind?: 'goods' | 'services' },
  onDone?: () => void,
): Promise<void> => {
  const receiptKind = opts.receiptKind || 'goods';
  const params: Record<string, string> = {
    payment_type: opts.cardPayment ? 'card' : 'cash',
    receipt_kind: receiptKind,
  };
  const key = `fiscalize-${receiptKind}-${orderId}`;
  const label = receiptKind === 'services' ? 'чека услуг' : 'чека товаров';
  message.loading({ content: `Пробитие ${label} — ждём 1С…`, key, duration: 0 });
  const res = await runPostingCommand('fiscalize_receipt', orderId, params);
  if (res.status === 'done') {
    message.success({ content: `Чек ${receiptKind === 'services' ? 'услуг' : 'товаров'} пробит в 1С`, key, duration: 5 });
    onDone?.();
  } else if (res.status === 'error') {
    message.error({ content: res.error, key, duration: 10 });
  } else {
    message.warning({ content: '1С пока не ответила — проверьте агента на ПК кассира', key, duration: 6 });
  }
};
